import * as React from 'react';
import { Button, ButtonProps, Tooltip } from '@fluentui/react-components';
import { Checkmark20Regular, Copy20Regular } from '@fluentui/react-icons';
import { copyText } from '../../lib/clipboard';
import { useAppToast } from '../../app/ToastContext';

export interface ICopyButtonProps {
    value?: string;
    /** Tooltip / aria label, e.g. «Копировать ссылку». */
    label: string;
    /** Toast text after a successful copy. */
    successMessage?: string;
    size?: ButtonProps['size'];
    appearance?: ButtonProps['appearance'];
    className?: string;
    /** Render a text button instead of an icon-only one. */
    text?: string;
}

const CopyButton: React.FC<ICopyButtonProps> = ({
    value, label, successMessage, size = 'small', appearance = 'subtle', className, text,
}) => {
    const toast = useAppToast();
    const [copied, setCopied] = React.useState(false);

    React.useEffect(() => {
        if (!copied) return;
        const id = window.setTimeout(() => setCopied(false), 1600);
        return () => window.clearTimeout(id);
    }, [copied]);

    const onClick = async (event: React.MouseEvent): Promise<void> => {
        event.stopPropagation();
        event.preventDefault();
        if (!value) return;
        const ok = await copyText(value);
        if (ok) {
            setCopied(true);
            if (successMessage) toast.success(successMessage);
        } else {
            toast.error('Не удалось скопировать в буфер обмена.');
        }
    };

    const button = (
        <Button
            className={className}
            appearance={appearance}
            size={size}
            icon={copied ? <Checkmark20Regular /> : <Copy20Regular />}
            aria-label={label}
            disabled={!value}
            onClick={(event) => { void onClick(event); }}
        >
            {text}
        </Button>
    );

    if (text) return button;
    return (
        <Tooltip content={copied ? 'Скопировано' : label} relationship="label" withArrow>
            {button}
        </Tooltip>
    );
};

export default CopyButton;
